export type Role = 'RESIDENT' | 'HEALTHCARE_PROVIDER' | 'INSTALLER' | 'WATCHERR_EMPLOYEE' | 'SERVICE';

export const ROLE_RANK: Record<Role, number> = {
  RESIDENT: 0,
  HEALTHCARE_PROVIDER: 1,
  INSTALLER: 2,
  WATCHERR_EMPLOYEE: 3,
  SERVICE: 4,
};

export type FieldType =
  | 'TextField'
  | 'PasswordField'
  | 'NumberField'
  | 'ToggleSwitch'
  | 'DropDown'
  | 'TextArea'
  | 'CodeBlock'
  | 'StatusBlock'
  | 'Polygon'
  // A checkbox over a setting whose off state is a value rather than an absence. With choices of
  // its own it renders them inside itself, so one parameter answers "is this on" and "which kind".
  | 'CheckboxChoice'
  // A radio list whose options carry a description and may reveal a field of their own.
  | 'ChoiceList'
  // Zone restriction. Its options are a site's zones, which the configuration service does not
  // know, so no definition can supply them -- the control resolves them itself.
  | 'ZonePicker';

export type Constraint =
  | { notNull: { message: string } }
  // An open-ended bound omits its side entirely.
  | { range: { min?: number; max?: number; message: string } }
  | { regex: { regex: string; message: string } }
  | {
      comparator: {
        type: 'greaterThan' | 'greaterThanOrEqual' | 'lessThan' | 'lessThanOrEqual' | 'equalTo';
        field: string;
        message: string;
        // The comparison does not apply while `field` holds `equals` — man-down uses it for 0,
        // which means disabled and must accept any warning threshold.
        skip_if?: { field: string; equals: FieldValue };
      };
    }
  | {
      validation_function: {
        name: string;
        params?: string[];
        message: string;
        // Which of a multi-parameter control's keys the rule judges. A control owning several has
        // no single value of its own, so `currentValue` alone would not say.
        field?: string;
      };
    };

export interface DropDownOptionObject {
  value: string | number | boolean;
  label_key?: string;
  props?: FieldValues;
  // A line under the label, for a control that explains each option rather than listing them.
  description_key?: string;
  // Field ids rendered inside this option while it is the selected one.
  reveals?: string[];
  // Offered only for these device types; absent means every type the form is served for.
  only_for?: string[];
}

// An option whose label is its own value arrives bare. The timezone list is ~595 of them, so
// spelling out `label_key` for each would be more than half the payload.
export type DropDownOption = string | DropDownOptionObject;

export const isOptionObject = (option: DropDownOption): option is DropDownOptionObject => typeof option !== 'string';

export interface FieldAction {
  label_key: string;
  variant?: 'primary' | 'ghost' | 'danger';
}

export interface Field {
  id: string;
  type: FieldType;
  title: string;
  tooltip?: string;
  // Always on screen, unlike `tooltip`, which is revealed on demand. A checkbox standing for a
  // whole alarm is the case: what it does does not fit in its label.
  description_key?: string;
  unit?: string;
  default?: string | number | boolean;
  placeholder?: string;
  span?: 1 | 2 | 3 | 4;
  sub_section?: string;
  enabled?: boolean;
  required_role?: Role;
  required_permissions?: string[];
  supported_since?: string;
  deprecated_since?: string;
  highlight?: boolean;
  options?: DropDownOption[];
  visible_if?: string;
  enabled_if?: string;
  device_model_level?: boolean;
  // One control writing several config keys; `source_keys` names them.
  synthetic?: boolean;
  source_keys?: string[];
  source_defaults?: FieldValues;
  advanced?: boolean;
  // Field ids rendered inside this one while it is on. `visible_if` cannot express this: that
  // hides a sibling at the same level, where these are drawn within the control itself.
  reveals?: string[];
  // What this field stores when it is off, where that is a value rather than `false`.
  off_value?: string | number | boolean;
  // Rendered only for these device types; absent means every type the form is served for.
  only_for?: string[];
  constraints?: Constraint[];
  format?: string;
  action?: FieldAction;
}

export interface SubSection {
  id: string;
  title_key: string;
}

export type SubProfileOption = DropDownOptionObject & { label_key: string; value: string };

export interface SubProfile {
  label_key: string;
  selected?: string;
  options: SubProfileOption[];
}

export interface Section {
  title_key: string;
  icon?: string;
  icon_color?: string;
  cols?: 1 | 2 | 3 | 4;
  collapsed?: boolean;
  advanced_toggle?: boolean;
  sub_sections?: SubSection[];
  fields: Field[];
}

export type LayoutRow = { section: string } | { split: string[] };

export interface SchemaAction {
  id: string;
  label_key: string;
  variant?: 'primary' | 'ghost' | 'danger';
  icon?: string;
  placement?: 'header' | 'actionbar';
  required_role?: Role;
  required_permissions?: string[];
  split?: { id: string; label_key: string }[];
  confirm?: { title_key: string; body_key: string };
  // Editing scopes the action belongs to, matching `ConfigScope['kind']`. Absent means every
  // scope. Gated the same way as the role and permissions above: the schema declares it and the
  // form filters on it.
  scopes?: string[];
}

export interface SchemaMeta {
  title_key: string;
  subtitle_key: string;
  schema_version?: string;
  device_type?: string;
  // Which form was served. Resolved by the backend from the caller's profile, not requested.
  variant?: string;
  // The firmware the schema was built for; shapes defaults and which fields exist.
  sw_version?: string | null;
  profile?: { selected: string; options: string[] };
}

export interface Schema {
  meta: SchemaMeta;
  layout: LayoutRow[];
  sections: Record<string, Section>;
  actions: SchemaAction[];
}

/**
 * A value a field can hold.
 *
 * Admits lists and objects because some fields are neither a scalar nor a choice: a perimeter is
 * a GeoJSON feature, and a resident's zone restriction is a list of ids. Compare two of these
 * with `same`, never `!==` -- no two objects are ever `===`, so a strict comparison reports every
 * one of them as changed on every render.
 */
export type FieldValue = string | number | boolean | null | undefined | FieldValue[] | { [key: string]: FieldValue };
export type FieldValues = Record<string, FieldValue>;

/**
 * The same value as far as the form is concerned.
 *
 * Lives beside `FieldValue` rather than with the layer code that first needed it: it is the
 * equality rule for this type, and every comparison of two field values has to use it.
 */
export const same = (a: FieldValue, b: FieldValue): boolean =>
  typeof a === 'object' || typeof b === 'object' ? JSON.stringify(a) === JSON.stringify(b) : a === b;

export const collectAllFields = (schema: Schema): Field[] => {
  const out: Field[] = [];
  for (const section of Object.values(schema.sections)) {
    for (const f of section.fields) out.push(f);
  }
  return out;
};
